import { normalizeMjaiTile } from "../tiles";
import type { ParseResult } from "../types";

// Parses a mjai JSONL log. Mostly passthrough: normalizes tile aliases
// (0m/1z... -> canonical), renames uradora_markers -> ura_markers (the key the
// viewer reads), and synthesizes absolute `scores` on hora/ryukyoku from the
// running scores when the log only carries `deltas` (lean M-League-style logs),
// so the viewer's score panel updates on round end.

function normalizeTiles(value: unknown): unknown {
    if (typeof value === "string") return normalizeMjaiTile(value);
    if (Array.isArray(value)) return value.map(normalizeTiles);
    return value;
}

const TILE_FIELDS = ["pai", "consumed", "tehais", "dora_marker", "ura_markers", "hora_tehais"];

export function parseMjai(text: string): ParseResult {
    const events: MjaiEvent[] = [];
    const warnings: string[] = [];
    const lines = text.split("\n");

    for (let i = 0; i < lines.length; i++) {
        const line = (lines[i] as string).trim();
        if (line === "") continue;
        let raw: unknown;
        try {
            raw = JSON.parse(line);
        } catch (e) {
            throw new Error(`mjai line ${i + 1}: invalid JSON (${(e as Error).message})`);
        }
        if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
            throw new Error(`mjai line ${i + 1}: expected a JSON object`);
        }
        const ev = { ...(raw as MjaiEvent) };
        if (typeof ev.type !== "string") {
            throw new Error(`mjai line ${i + 1}: missing "type"`);
        }
        if (ev.uradora_markers !== undefined && ev.ura_markers === undefined) {
            ev.ura_markers = ev.uradora_markers;
            delete ev.uradora_markers;
        }
        for (const field of TILE_FIELDS) {
            if (ev[field] !== undefined) {
                try {
                    ev[field] = normalizeTiles(ev[field]);
                } catch (e) {
                    throw new Error(`mjai line ${i + 1}: ${(e as Error).message}`);
                }
            }
        }
        events.push(ev);
    }

    if (events.length === 0) {
        throw new Error("mjai log contains no events");
    }

    synthesizeScores(events);

    const last = events[events.length - 1] as MjaiEvent;
    if (last.type === "hora" || last.type === "ryukyoku") {
        events.push({ type: "end_kyoku" });
        warnings.push("log was truncated: appended missing end_kyoku");
    }
    if ((events[events.length - 1] as MjaiEvent).type === "end_kyoku") {
        events.push({ type: "end_game" });
        warnings.push("log was truncated: appended missing end_game");
    }

    return { events, warnings };
}

/** Fill in absolute `scores` on hora/ryukyoku events that only carry `deltas`. */
function synthesizeScores(events: MjaiEvent[]): void {
    let running: number[] | null = null;
    for (const ev of events) {
        switch (ev.type) {
            case "start_kyoku":
                if (Array.isArray(ev.scores)) running = [...(ev.scores as number[])];
                break;
            case "reach_accepted":
                if (running && typeof ev.actor === "number") {
                    running[ev.actor] = (running[ev.actor] as number) - 1000;
                }
                break;
            case "hora":
            case "ryukyoku":
                if (Array.isArray(ev.scores)) {
                    running = [...(ev.scores as number[])];
                } else if (running && Array.isArray(ev.deltas)) {
                    running = running.map((s, i) => s + ((ev.deltas as number[])[i] ?? 0));
                    ev.scores = [...running];
                }
                break;
        }
    }
}
